import React, { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

interface McpKeyItem {
  id: number;
  label: string;
  keyPrefix: string;
  createdAt: string;
  createdByName: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  requestsToday: number;
  requestsThisMonth: number;
  requestsDayStart: string;
  requestsMonthStart: string;
}

interface McpKeysData {
  keys: McpKeyItem[];
  monthlyLimit: number;
}

function UsageBar({ used, limit }: { used: number; limit: number }) {
  if (limit <= 0) return null;
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const color =
    pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-400" : "bg-primary";
  return (
    <div className="w-full">
      <div className="flex justify-between text-xs text-slate-500 mb-0.5">
        <span>{used.toLocaleString()} used</span>
        <span>{limit.toLocaleString()} limit</span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function McpKeysPanel() {
  const api = useApi();
  const { displayToast } = useToastAlert();
  const [data, setData] = useState<McpKeysData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newExpiry, setNewExpiry] = useState("");
  const [creating, setCreating] = useState(false);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [revealedKeyId, setRevealedKeyId] = useState<number | null>(null);
  const [confirmRevokeId, setConfirmRevokeId] = useState<number | null>(null);

  const fetchKeys = useCallback(async () => {
    try {
      const resp = await api.admin.get("McpApiKeys");
      if (resp.ok) setData(await resp.json());
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  async function createKey() {
    if (!newLabel.trim()) return;
    setCreating(true);
    try {
      const body: Record<string, unknown> = { label: newLabel.trim() };
      if (newExpiry) body.expiresAt = new Date(newExpiry).toISOString();
      const resp = await api.admin.post("McpApiKeys", body);
      if (resp.ok) {
        const result = await resp.json();
        setRevealedKey(result.rawKey);
        setRevealedKeyId(result.id);
        setShowCreate(false);
        setNewLabel("");
        setNewExpiry("");
        await fetchKeys();
      } else {
        const err = await resp.json().catch(() => ({}));
        displayToast(Severity.Error, err.error ?? "Failed to create MCP key.");
      }
    } catch {
      displayToast(Severity.Error, "Failed to create MCP key.");
    } finally {
      setCreating(false);
    }
  }

  async function revokeKey(id: number) {
    try {
      const resp = await api.admin.delete(`McpApiKeys/${id}`);
      if (resp.ok) {
        displayToast(Severity.Success, "MCP key revoked.");
        setConfirmRevokeId(null);
        if (revealedKeyId === id) { setRevealedKey(null); setRevealedKeyId(null); }
        await fetchKeys();
      } else {
        displayToast(Severity.Error, "Failed to revoke key.");
      }
    } catch {
      displayToast(Severity.Error, "Failed to revoke key.");
    }
  }

  function formatDate(iso: string | null) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }

  const monthlyLimit = data?.monthlyLimit ?? 0;
  const totalThisMonth = (data?.keys ?? []).reduce((s, k) => s + k.requestsThisMonth, 0);
  const totalToday = (data?.keys ?? []).reduce((s, k) => s + k.requestsToday, 0);
  const isUnlimited = monthlyLimit === -1;
  const isBlocked = data !== null && monthlyLimit === 0;

  return (
    <div className="p-6 max-w-3xl">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-800">MCP Keys</h2>
        <p className="text-sm text-slate-500 mt-1">
          Connect Claude, Claude Code, and other AI agents directly to your Velaro account using{" "}
          <code className="font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">vel_live_…</code> keys.
          Each key is site-isolated and shown only once.
        </p>
      </div>

      {/* Usage summary */}
      {data && !isBlocked && (
        <div className="mb-5 p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-3">
          <div className="flex gap-6 text-sm">
            <div>
              <span className="text-slate-500">Today</span>
              <p className="font-semibold text-slate-800 text-lg leading-tight">{totalToday.toLocaleString()}</p>
            </div>
            <div>
              <span className="text-slate-500">This month</span>
              <p className="font-semibold text-slate-800 text-lg leading-tight">{totalThisMonth.toLocaleString()}</p>
            </div>
            <div>
              <span className="text-slate-500">Monthly limit</span>
              <p className="font-semibold text-slate-800 text-lg leading-tight">
                {isUnlimited ? "Unlimited" : monthlyLimit > 0 ? monthlyLimit.toLocaleString() : "—"}
              </p>
            </div>
          </div>
          {monthlyLimit > 0 && !isUnlimited && (
            <UsageBar used={totalThisMonth} limit={monthlyLimit} />
          )}
          {monthlyLimit > 0 && !isUnlimited && totalThisMonth / monthlyLimit >= 0.8 && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              You've used {Math.round((totalThisMonth / monthlyLimit) * 100)}% of your monthly MCP calls.{" "}
              <a href="mailto:support@velaro.com" className="underline">Contact us to increase your limit.</a>
            </p>
          )}
        </div>
      )}

      {/* Blocked state */}
      {isBlocked && (
        <div className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          MCP access is not included in your current plan.{" "}
          <a href="mailto:support@velaro.com" className="underline font-medium">Contact us to enable it.</a>
        </div>
      )}

      {/* Revealed key banner — shown once */}
      {revealedKey && (
        <div className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm font-semibold text-amber-800 mb-2">Copy your MCP key — it won't be shown again</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white border border-amber-300 rounded px-3 py-2 text-sm font-mono text-slate-800 overflow-x-auto">
              {revealedKey}
            </code>
            <button
              onClick={() => { navigator.clipboard.writeText(revealedKey); displayToast(Severity.Success, "Copied!"); }}
              className="shrink-0 px-3 py-2 text-xs font-medium bg-amber-600 text-white rounded hover:bg-amber-700"
            >
              Copy
            </button>
          </div>
          <div className="mt-3 text-xs text-amber-700 space-y-1">
            <p>Add to <code className="font-mono bg-white/60 px-1 rounded">~/.claude/settings.json</code>:</p>
            <pre className="bg-white/60 border border-amber-200 rounded px-3 py-2 text-xs overflow-x-auto">{`"mcpServers": {
  "velaro": {
    "command": "npx",
    "args": ["-y", "@velaro/mcp-server"],
    "env": { "VELARO_MCP_KEY": "${revealedKey}" }
  }
}`}</pre>
          </div>
          <button
            onClick={() => { setRevealedKey(null); setRevealedKeyId(null); }}
            className="mt-2 text-xs text-amber-700 underline"
          >
            I've copied it, dismiss
          </button>
        </div>
      )}

      {/* Key list */}
      {loading ? (
        <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>
      ) : !Array.isArray(data?.keys) || data!.keys.length === 0 ? (
        <div className="text-sm text-slate-400 py-8 text-center border border-dashed border-slate-200 rounded-lg">
          No MCP keys yet. Create one to connect Claude or another AI agent.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden mb-5">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Label</th>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Prefix</th>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Today</th>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">This month</th>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Last used</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {data!.keys.map((k, i) => (
                <tr
                  key={k.id}
                  className={`border-b border-slate-100 ${
                    revealedKeyId === k.id ? "bg-amber-50" : i % 2 === 0 ? "bg-white" : "bg-slate-50/40"
                  }`}
                >
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-slate-800">{k.label}</div>
                    {k.createdByName && (
                      <div className="text-xs text-slate-400 mt-0.5">Created by {k.createdByName}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{k.keyPrefix}…</td>
                  <td className="px-4 py-2.5 text-slate-700">{k.requestsToday.toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-slate-700">
                    <div className="flex items-center gap-2">
                      <span>{k.requestsThisMonth.toLocaleString()}</span>
                      {monthlyLimit > 0 && !isUnlimited && (
                        <div className="w-16 h-1 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              k.requestsThisMonth / monthlyLimit >= 0.9
                                ? "bg-red-400"
                                : k.requestsThisMonth / monthlyLimit >= 0.7
                                ? "bg-amber-400"
                                : "bg-primary-hover"
                            }`}
                            style={{ width: `${Math.min(100, (k.requestsThisMonth / monthlyLimit) * 100)}%` }}
                          />
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-slate-500">{formatDate(k.lastUsedAt)}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex gap-2 justify-end">
                      {confirmRevokeId === k.id ? (
                        <>
                          <button
                            onClick={() => revokeKey(k.id)}
                            className="text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                          >
                            Confirm revoke
                          </button>
                          <button
                            onClick={() => setConfirmRevokeId(null)}
                            className="text-xs px-2 py-1 bg-slate-200 text-slate-700 rounded hover:bg-slate-300"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setConfirmRevokeId(k.id)}
                          className="text-xs text-red-500 hover:text-red-700 underline"
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create form */}
      {!isBlocked && (
        showCreate ? (
          <div className="border border-slate-200 rounded-lg p-4 mb-5 bg-slate-50">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">New MCP Key</h3>
            <div className="mb-3">
              <label className="block text-xs font-medium text-slate-600 mb-1">Label</label>
              <input
                type="text"
                placeholder="e.g. Claude Code, Production Bot, Moshky"
                value={newLabel}
                onChange={e => setNewLabel(e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
                autoFocus
                maxLength={200}
                onKeyDown={e => e.key === "Enter" && createKey()}
              />
            </div>
            <div className="mb-4">
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Expiry <span className="font-normal text-slate-400">(optional)</span>
              </label>
              <input
                type="date"
                value={newExpiry}
                onChange={e => setNewExpiry(e.target.value)}
                className="border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={createKey}
                disabled={creating || !newLabel.trim()}
                className="px-4 py-2 text-sm font-medium bg-primary text-white rounded hover:bg-primary-active disabled:opacity-50"
              >
                {creating ? "Creating…" : "Create key"}
              </button>
              <button
                onClick={() => { setShowCreate(false); setNewLabel(""); setNewExpiry(""); }}
                className="px-4 py-2 text-sm font-medium bg-white border border-slate-300 text-slate-700 rounded hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowCreate(true)}
            className="mb-6 px-4 py-2 text-sm font-medium bg-primary text-white rounded hover:bg-primary-active"
          >
            + Create MCP key
          </button>
        )
      )}

      {/* Quick setup reference */}
      <div className="border border-slate-200 rounded-lg p-4 bg-white">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Quick setup</h3>
        <div className="space-y-4 text-sm">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Claude Code (~/.claude/settings.json)</p>
            <pre className="bg-slate-50 border border-slate-200 rounded px-3 py-2 text-xs font-mono text-slate-700 overflow-x-auto">{`"mcpServers": {
  "velaro": {
    "command": "npx",
    "args": ["-y", "@velaro/mcp-server"],
    "env": { "VELARO_MCP_KEY": "vel_live_..." }
  }
}`}</pre>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Hosted HTTP endpoint (no Node required)</p>
            <pre className="bg-slate-50 border border-slate-200 rounded px-3 py-2 text-xs font-mono text-slate-700 overflow-x-auto">{`"mcpServers": {
  "velaro": {
    "url": "https://api-admin-us-east.velaro.com/mcp",
    "headers": { "Authorization": "Bearer vel_live_..." }
  }
}`}</pre>
          </div>
          <div className="pt-1 border-t border-slate-100 text-xs text-slate-500">
            Keys are site-isolated — each key can only access data for your Velaro account.
            Create one key per integration so you can revoke them independently.
          </div>
        </div>
      </div>
    </div>
  );
}
